import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import { usePhotosLikeDataRefetch } from 'dibs-interiors-photos/exports/usePhotosLikeDataRefetch';
import { trackSavePhoto } from 'dibs-interiors-photos/exports/trackingHelpers';
import SocialIconsWrapper from '../../sharedComponents/interiors/SocialIconsWrapper';

import { type PhotosSearchBrowseSocialIcons_photo$key } from './__generated__/PhotosSearchBrowseSocialIcons_photo.graphql';
import { type PhotosSearchBrowseSocialIcons_photos$key } from './__generated__/PhotosSearchBrowseSocialIcons_photos.graphql';

type Props = {
    photo: PhotosSearchBrowseSocialIcons_photo$key | null | undefined;
    photos: PhotosSearchBrowseSocialIcons_photos$key | null | undefined;
    userId: string;
};

const PhotosSearchBrowseSocialIcons: FC<Props> = props => {
    const { photos: photosRef, photo: photoRef, userId } = props;

    const photos = useFragment(
        graphql`
            fragment PhotosSearchBrowseSocialIcons_photos on PhotosSearchBrowseQueryConnection {
                edges {
                    node {
                        ...usePhotosLikeDataRefetch_photos
                    }
                }
            }
        `,
        photosRef
    );

    const photo = useFragment(
        graphql`
            fragment PhotosSearchBrowseSocialIcons_photo on PhotosSearchBrowsePhoto {
                id
                path
                url
                serviceId
                project {
                    name
                    url
                }
                designFirm {
                    name
                }
                photoLike {
                    totalPhotoLikes
                }
            }
        `,
        photoRef
    );

    const photoPath = photo?.path || '';

    const photoNodes =
        (photos?.edges || []).map(edge => edge?.node || null).filter(filterNulls) || null;
    const { photoLikeDataMap, isRefetching } = usePhotosLikeDataRefetch({
        photosRef: photoNodes,
        userId,
    });
    const photoLikeData = photoLikeDataMap.get(photo?.serviceId || '');

    const firmName = photo?.designFirm?.name || '';
    const project = photo?.project;
    const projectName = project?.name || '';
    const projectUrl = project?.url || '';

    return (
        <SocialIconsWrapper
            photoNodeId={photo?.id || ''}
            photoId={photo?.serviceId || ''}
            photoLikeId={photoLikeData?.photoLikeId || ''}
            onPhotoLikeAdded={() => trackSavePhoto({ label: 'photos' })}
            firmName={firmName}
            projectName={projectName}
            projectUrl={projectUrl}
            photoUrl={photo?.url || ''}
            showPhotoHeart={!isRefetching}
            totalPhotoLikes={photo?.photoLike?.totalPhotoLikes || 0}
            photoPath={photoPath}
        />
    );
};

export default PhotosSearchBrowseSocialIcons;
